import { Matrix4 } from 'three'
import {
  dumpUnitGeometry,
  getUnitOBB,
  isOccupyingUnit,
  obbIntersects,
  obbOverlapMargin,
  type UnitGeometry,
  type UnitGeometryDump,
} from './unitObb'

export type CandidatePose = {
  matrix?: Matrix4 | null
  x?: number
  y?: number
  z?: number
  rotation?: number
  width?: number
  height?: number
  thickness?: number
}

export type PoseValidation =
  | { valid: true }
  | {
      valid: false
      reason: 'GEOMETRIC_COLLISION'
      collidingUnitIds: string[]
      candidate: UnitGeometryDump
      collidingUnits: UnitGeometryDump[]
    }

export type UnitCollisionPair = {
  unitA: string
  unitB: string
  intersection: true
  overlapMm: number
  unitADump: UnitGeometryDump
  unitBDump: UnitGeometryDump
}

type ItemMap = Map<string, UnitGeometry>

function poseMatrix(unit: UnitGeometry, pose?: CandidatePose): Matrix4 | null {
  if (pose?.matrix) return pose.matrix
  if (!unit.matrix) return null
  if (pose?.x == null && pose?.y == null && pose?.z == null && pose?.rotation == null) {
    return unit.matrix
  }
  const next = unit.matrix.clone()
  if (pose?.x != null || pose?.y != null || pose?.z != null) {
    const x = pose?.x ?? unit.x ?? 0
    const y = pose?.y ?? unit.y ?? 0
    const z = pose?.z ?? unit.z ?? 0
    next.setPosition(x, y, z)
  }
  return next
}

function posedUnit(unit: UnitGeometry, pose?: CandidatePose): UnitGeometry {
  if (!pose) return unit
  return {
    ...unit,
    matrix: poseMatrix(unit, pose),
    width: pose.width ?? unit.width,
    height: pose.height ?? unit.height,
    thickness: pose.thickness ?? unit.thickness,
    x: pose.x ?? unit.x,
    y: pose.y ?? unit.y,
    z: pose.z ?? unit.z,
    rotation: pose.rotation ?? unit.rotation,
  }
}

/**
 * Comprueba la pose candidata contra el resto de unidades ocupantes.
 * No escribe estado. `items` debe ser la colección vigente en el momento
 * de decidir el commit.
 */
export function validateUnitPose(
  unit: UnitGeometry,
  pose: CandidatePose | undefined,
  items: ItemMap,
  selfId?: string,
): PoseValidation {
  const posed = posedUnit(unit, pose)
  const id = String(selfId ?? unit.id ?? '')
  if (!isOccupyingUnit(posed)) return { valid: true }

  const candidateObb = getUnitOBB(posed)
  if (!candidateObb) return { valid: true }

  const collidingUnitIds: string[] = []
  const collidingUnits: UnitGeometryDump[] = []

  items.forEach((other, otherKey) => {
    const otherId = String(other.id ?? otherKey)
    if (otherId === id || String(otherKey) === id) return
    if (!isOccupyingUnit(other)) return
    const otherObb = getUnitOBB(other)
    if (!otherObb) return
    if (!obbIntersects(candidateObb, otherObb)) return
    collidingUnitIds.push(otherId)
    collidingUnits.push(dumpUnitGeometry(other, otherId))
  })

  if (collidingUnitIds.length === 0) return { valid: true }

  return {
    valid: false,
    reason: 'GEOMETRIC_COLLISION',
    collidingUnitIds,
    candidate: dumpUnitGeometry(posed, id),
    collidingUnits,
  }
}

/** Pares de unidades ocupantes cuyo volumen común supera la tolerancia. */
export function findAllUnitCollisions(items: ItemMap): UnitCollisionPair[] {
  const occupying: Array<{ id: string; item: UnitGeometry }> = []
  items.forEach((item, key) => {
    if (!isOccupyingUnit(item)) return
    occupying.push({ id: String(item.id ?? key), item })
  })

  const pairs: UnitCollisionPair[] = []
  for (let i = 0; i < occupying.length; i++) {
    const a = occupying[i]
    const obbA = getUnitOBB(a.item)
    if (!obbA) continue
    for (let j = i + 1; j < occupying.length; j++) {
      const b = occupying[j]
      if (a.id === b.id) continue
      const obbB = getUnitOBB(b.item)
      if (!obbB) continue
      if (!obbIntersects(obbA, obbB)) continue
      pairs.push({
        unitA: a.id,
        unitB: b.id,
        intersection: true,
        overlapMm: obbOverlapMargin(obbA, obbB),
        unitADump: dumpUnitGeometry(a.item, a.id),
        unitBDump: dumpUnitGeometry(b.item, b.id),
      })
    }
  }
  return pairs
}

export function formatIntegrityError(pairs: UnitCollisionPair[]): string {
  const lines = pairs.map(
    (pair) =>
      `[3D INTEGRITY ERROR]\nUnit ${pair.unitA} intersects Unit ${pair.unitB}\n` +
      `${pair.unitA}: ${JSON.stringify(pair.unitADump)}\n` +
      `${pair.unitB}: ${JSON.stringify(pair.unitBDump)}`,
  )
  return lines.join('\n')
}

/** Lanza si hay alguna colisión. Pensado para diagnóstico, no para el commit. */
export function assertNoUnitCollisions(items: ItemMap): void {
  const pairs = findAllUnitCollisions(items)
  if (pairs.length === 0) return
  throw new Error(formatIntegrityError(pairs))
}
